import React from "react"
import CelticDropCap from "design_system/application/components/celtic_drop_cap"
import { useRemarkSync } from "react-remark"
import wrapAmpersand from "design_system/utils/ampersand_wrapper"
import rehypeRaw from "rehype-raw"
import classNames from "classnames"
import RemarkDropcap from "design_system/utils/remark_dropcap"
import { usernameFromMentionHref } from "design_system/utils/user_mention"
import rehypeExternalLinks from "rehype-external-links"
import rehypeSanitize, { defaultSchema } from "rehype-sanitize"
import { twMerge } from "tailwind-merge"
import { UserPopover } from "@love-irish/user-popover"

type DropcapSize = "xs" | "sm" | "md" | "lg" | "xl"

interface ContentProps {
  /**
   * Markdown content to render
   */
  content?: string
  /**
   * Enable Celtic drop cap on first letter. Pass size string to customize.
   */
  dropcap?: boolean | DropcapSize
  /**
   * Additional CSS classes
   */
  className?: string
  /**
   * Component to render as (div, section, article, etc.)
   */
  as?: React.ElementType
  /**
   * Whether this markdown is trusted/static (team-authored editorial) rather than user-generated. Drives how
   * EXTERNAL links are marked: content is treated as UGC by default (external links get
   * `rel="ugc nofollow noopener noreferrer"` so search engines don't follow user-submitted outbound links);
   * pass `trusted` for editorial content to drop ugc/nofollow. Either way external links open in a new tab
   * with tab-nabbing protection.
   */
  trusted?: boolean
  [key: string]: any
}

/**
 * `rel` tokens for external markdown links. UGC (the default) marks user-submitted outbound links with
 * `ugc` + `nofollow` (SEO: don't endorse/pass rank to them); trusted/static content drops those. Both keep
 * `noopener noreferrer` so `target="_blank"` can't be abused for tab-nabbing.
 */
export const externalLinkRel = (trusted: boolean): string[] =>
  trusted
    ? ["noopener", "noreferrer"]
    : ["ugc", "nofollow", "noopener", "noreferrer"]

/**
 * Content component that renders markdown with optional Celtic drop cap
 */
// Strip a leading YAML front-matter block (`--- ... ---`). It carries hidden context for search/embeddings
// (see Library::ContentScaffold) and must never render as an <hr> + visible `key: value` text on the page.
//
// Careful not to eat real horizontal rules: `---` is also a Markdown thematic break, so we only treat a
// block as front matter when it (a) sits at the very start of the content (no leading whitespace) and
// (b) opens with a `key:` line. A leading hr (`---` followed by a blank line or prose) has no `key:` on
// the next line, so it's left alone; hrs anywhere below the first line are never at position 0 to begin with.
const stripFrontMatter = (text: string) =>
  text.replace(/^﻿?---[ \t]*\r?\n(?=[^\n]*:)[\s\S]*?\r?\n---[ \t]*(?:\r?\n|$)/, "")

/**
 * The `a` renderer for markdown content: a `/@username` link becomes the profile hover card (UserPopover);
 * everything else is a plain anchor. Detection lives in the design_system markdown util, so this mirrors the
 * dropcap span override — the mention logic stays in the markdown pipeline. Exported for direct testing
 * (react-remark doesn't parse markdown under jsdom, so the full Content pipeline can't be exercised in jest).
 */
export const MarkdownAnchor = (props: any) => {
  const username = usernameFromMentionHref(props.href)
  if (username) {
    return <UserPopover username={username}>{props.children}</UserPopover>
  }

  return <a {...props} />
}

const Content = ({
  content = "",
  dropcap = false,
  className,
  as: Component = "div",
  trusted = false,
  ...props
}: ContentProps) => {
  const markdownContent = stripFrontMatter(content ?? "")
  const sanitize = defaultSchema

  // Needed since we mark dropcaps with a class to then render in the custom function below
  sanitize.attributes["*"].push("className")

  const reactContent = useRemarkSync(wrapAmpersand(markdownContent), {
    remarkPlugins: [[RemarkDropcap, { renderDropcap: dropcap }]],
    remarkToRehypeOptions: { allowDangerousHtml: true },
    rehypePlugins: [
      [rehypeSanitize, sanitize],
      rehypeRaw,
      // External links open in a new tab; rel marks them per trusted/UGC (see externalLinkRel).
      [rehypeExternalLinks, { rel: externalLinkRel(trusted), target: "_blank" }],
    ],
    rehypeReactOptions: {
      components: {
        span: (props: any) => {
          if (props.className === "dropcap" && dropcap) {
            return (
              <CelticDropCap
                className="m-0 mr-3"
                size={typeof dropcap === "string" ? dropcap : "md"}
                letter={props.children[0].toLowerCase()}
              />
            )
          }

          return <span {...props} />
        },
        // A `/@username` link becomes the profile hover card (same pipeline slot as the dropcap span above).
        a: MarkdownAnchor,
      },
    },
  })

  return (
    <Component
      data-testid="content"
      className={twMerge(
        "prose prose-ol:text-sm prose-ul:text-sm prose-hr:my-4 prose-blockquote:-ml-5 dark:prose-invert first-line:italic max-w-none! prose-headings:font-normal prose-img:mt-0!",
        className
      )}
      {...props}
    >
      {reactContent}
    </Component>
  )
}

export default Content
